import { Loader } from '@oriola-corporation/kronan-icons';
import React from 'react';
import { useAsync } from 'react-use';
import styled, { css } from 'styled-components';
import { getHighscore, HighscoreProps } from '../../api/api';
import { usePharmacy } from '../../contexts/Pharmacy';
import { filteredHighscore } from '../../util/clickCollect';
import { Card } from '../Card';
import { Header } from '../Header/Header';
import { Time } from './ClickCollectStats';

const OrderedList = styled.ol`
  padding-left: 24px;
  font-weight: bold;
  border-bottom: 2px solid #352e27;

  li {
    padding-bottom: 6px;
    margin: 6px 0;
  }
`;

const ListRow = styled.li<{ currentPharmacy?: boolean }>`
  ${({ currentPharmacy }) =>
    currentPharmacy &&
    css`
      color: darkred;
    `}
`;

const ListRowItem = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

interface Props {
  limit: number;
  items: HighscoreProps;
}

const Highscore = (props: Props) => {
  const { id } = usePharmacy();
  const { items, limit } = props;
  const filteredHighScore = filteredHighscore(items.pharmacies, limit);

  if (!filteredHighScore || filteredHighScore.length === 0) {
    return null;
  }

  return (
    <OrderedList>
      {filteredHighScore.map((score) => {
        const { pharmacyName, pharmacyId, promisedDeliveryPercentage, avgCollectingTime, value } =
          score;
        const isCurrentPharmacy = pharmacyId === id;
        return (
          <ListRow key={pharmacyId} currentPharmacy={isCurrentPharmacy} value={value}>
            <ListRowItem>
              <div>
                {pharmacyName ?? pharmacyId} {isCurrentPharmacy && '(ni)'}
              </div>
              <div>
                <Time time={avgCollectingTime} />({promisedDeliveryPercentage}%)
              </div>
            </ListRowItem>
          </ListRow>
        );
      })}
    </OrderedList>
  );
};

const ToolTipContent = () => {
  return (
    <>
      <p>
        Topplistan visar medianvärdet av de senaste 10 ordrarna. Inom parantes syns andelen ordrar
        som blivit klara inom 2 timmar, beräknat på de senaste 20 ordrarna.
      </p>
    </>
  );
};

export const ClickCollectHighscore = () => {
  const highscoreState = useAsync(async () => {
    return await getHighscore();
  });

  if (highscoreState.error) {
    return <Card>{JSON.stringify(highscoreState.error)}</Card>;
  }

  return (
    <>
      <Header tooltip={<ToolTipContent />}>Topplista plocktider</Header>
      <Card>
        {highscoreState.loading ? (
          <Loader isRotating />
        ) : highscoreState.value && highscoreState.value.numberOfPharmacies > 0 ? (
          <>
            <Highscore limit={5} items={highscoreState.value} />
            <p>
              Snittid för alla apotek: <Time time={highscoreState.value.avgDeliveryTime} /> (
              {highscoreState.value.promisedDeliveryPercentage}%)
            </p>
          </>
        ) : (
          <p>Topplistan innehåller ingen data just nu.</p>
        )}
      </Card>
    </>
  );
};
